import * as React from 'react';
import notify from '../../../../shared/feedback/notifications';
import Api from '../../../../shared/Api';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { Photographer } from '../BookingsCalendarTypes';
import TextField from '@mui/material/TextField';
import { Translate } from '../../../../shared/LanguageService';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { useQuery } from 'react-query';

type SmsModalLabels = {
    smsMessage: string;
    smsTemplate: string;
    smsWhen: string;
    smsTitle: string;
    smsClose: string;
    smsSend: string;
    smsSuccess: string;
    smsError: string;
    smsEmpty: string;
    smsExpiredDate: string;
    smsRecipient: (name: string, phoneNumber: string) => string;
};

type SmsModalProps = {
    showSmsModal: boolean;
    closeSmsModal: () => void;
    photographer: Photographer;
    labels: SmsModalLabels;
    smsTemplates: { content: string; key: number; name: string; }[];
    createSms: (phoneNumber: string, message: string, desiredSendTime?: Date) => Promise<any>;
};

export const StyledTextField = styled(TextField)(({ theme }) => ({
    'fieldset legend span': {
        textTransform: 'lowercase'
    }
}));

const SmsModal = ({
    showSmsModal,
    closeSmsModal,
    photographer,
    labels,
    smsTemplates,
    createSms
}: SmsModalProps) => {
    const [desiredSendTime, setDesiredSendTime] = React.useState<Date | null>(null);
    const [smsMessage, setSmsMessage] = React.useState<string>('');
    const [smsTemplate, setSmsTemplate] = React.useState<object | null>(null);
    const [errorMessage, setErrorMessage] = React.useState<boolean>(false);
    const [errorDate, setErrorDate] = React.useState<boolean>(false);

    const handleDesiredTimeChange = (newValue) => {
        setDesiredSendTime(newValue);
        if (newValue < new Date(Date.now())
            || new Date(newValue).toDateString() === 'Invalid Date') {
            setErrorDate(true);
        }
        if (newValue >= new Date(Date.now())
            || newValue === null) {
            setErrorDate(false);
        }
    };

    const handleSmsTemplateChange = (event, value) => {
        setErrorMessage(false);
        setSmsTemplate(value ? value : null);
        setSmsMessage(value ? value.content : '');
    };

    const handleSmsMessageChange = (event) => {
        setErrorMessage(false);
        setSmsMessage(event.target.value);
    };

    const handleClearMessage = () => {
        setErrorMessage(false);
        setSmsMessage('');
        setSmsTemplate(null);
    };

    const handleClose = () => {
        setSmsMessage('');
        setErrorMessage(false);
        setSmsTemplate(null);
        setDesiredSendTime(null);
        setErrorDate(false);
        closeSmsModal();
    };

    const sendSms = () => {
        if (smsMessage) {
            const result = createSms(photographer.phoneNumber, smsMessage, desiredSendTime);
            result
                .then(() => {
                    notify.success(labels.smsSuccess);
                    handleClose();
                })
                .catch(() => {
                    notify.failure(labels.smsError);
                });
        } else {
            setErrorMessage(true);
        }
    };

    return (
        <Dialog
            onClose={handleClose}
            open={showSmsModal}
            fullWidth
            sx={{ '& .MuiPaper-root': { overflowY: 'visible' } }}
        >
            <DialogTitle>
                {labels.smsTitle}
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Typography sx={{ margin: '0 0 24px 0', fontFamily: '"Gilroy"', fontSize: '14px' }}>
                    {labels.smsRecipient(
                        photographer.name,
                        photographer.phoneNumber
                    )}
                </Typography>
                <DateTimePicker
                    label={labels.smsWhen}
                    value={desiredSendTime}
                    inputFormat="yyyy-MM-dd HH:mm"
                    disablePast
                    onChange={handleDesiredTimeChange}
                    renderInput={(params) => (
                        <StyledTextField fullWidth {...params}
                            error={errorDate}
                            helperText={errorDate ? labels.smsExpiredDate : null}
                            sx={{
                                margin: '0 0 16px 0',
                                '& svg': {
                                    zIndex: '1'
                                }
                            }}
                        />
                    )}
                />
                {smsTemplates ? (
                    <Autocomplete
                        onChange={handleSmsTemplateChange}
                        disablePortal
                        fullWidth
                        id="sms-template"
                        value={smsTemplate}
                        getOptionLabel={(option) => option.name || ''}
                        isOptionEqualToValue={(option, value) =>
                            option.content === value.content || value.content === null
                        }
                        options={smsTemplates}
                        sx={{
                            margin: '0 0 16px 0',
                            '& svg': {
                                zIndex: '1'
                            }
                        }}
                        renderInput={(params) => (
                            <StyledTextField {...params} label={labels.smsTemplate} />
                        )}
                    />
                ) : null}
                <Box sx={{ position: 'relative' }}>
                    <StyledTextField
                        fullWidth
                        multiline
                        required
                        minRows={3}
                        error={Boolean(errorMessage)}
                        helperText={errorMessage ? labels.smsEmpty : ''}
                        value={smsMessage}
                        onChange={handleSmsMessageChange}
                        id="sms-message"
                        label={labels.smsMessage}
                        sx={{
                            margin: '0 0 16px 0',
                            '& .MuiOutlinedInput-input': {
                                width: '94%'
                            }
                        }}
                    />
                    <IconButton
                        aria-label="close"
                        onClick={handleClearMessage}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button autoFocus onClick={handleClose}>
                    {labels.smsClose}
                </Button>
                <Button autoFocus onClick={sendSms} variant="contained">
                    {labels.smsSend}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const SmsModalDataLayer = (props) => {
    const { photographer } = props;
    const getSmsTemplates = (photographerId: number) => {
        return useQuery('status', () =>
            new Api().get('/views/photographer-calendars/get-sms-templates/' + photographerId)
        );
    };
    const smsTemplates = getSmsTemplates(photographer.id);

    const createSms = (phoneNumber: string, message: string, desiredSendTime?: Date) => {
        return new Api().post('/views/photographer-calendars/create-sms', {
            toPhoneNumber: phoneNumber,
            body: message,
            desiredSendTime: desiredSendTime
        });
    };

    return <SmsModal {...props} smsTemplates={smsTemplates.data} createSms={createSms} />;
};

const SmsModalTranslationLayer = (props): any => {
    let labels: SmsModalLabels = {
        smsMessage: Translate('sms-modal/body'),
        smsTemplate: Translate('sms-modal/template'),
        smsWhen: Translate('sms-modal/when'),
        smsTitle: Translate('sms-modal/title'),
        smsClose: Translate('sms-modal/close'),
        smsSend: Translate('sms-modal/send'),
        smsSuccess: Translate('sms-modal/success'),
        smsError: Translate('sms-modal/error'),
        smsEmpty: Translate('sms-modal/empty'),
        smsExpiredDate: Translate('sms-modal/expired-date'),
        smsRecipient: (name, phoneNumber) => Translate('sms-modal/recipient', { name, phoneNumber })
    };
    return <SmsModalDataLayer {...props} labels={labels} />;
};

export default SmsModalTranslationLayer;
